"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";

const TAG_GAP = 8;
const MAX_VISIBLE_TAGS = 2;

const tagClassName =
  "flex h-[34px] shrink-0 items-center rounded-sm border bg-brand-white px-2 py-1.5 font-medium text-brand-grey5 text-sm capitalize";

export type MealTagListProps = {
  tags: string[];
  expandLabel: string;
  className?: string;
};

export const MealTagList = ({
  tags,
  expandLabel,
  className,
}: MealTagListProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(
    Math.min(tags.length, MAX_VISIBLE_TAGS),
  );
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;

    if (!(row && measure)) {
      return;
    }

    const recalculate = () => {
      const available = row.clientWidth;
      const items = Array.from(measure.children) as HTMLElement[];
      const toggleWidth = items.at(-1)?.offsetWidth ?? 0;
      const allWidths = items.slice(0, -1).map((item) => item.offsetWidth);
      const widths = allWidths.slice(0, MAX_VISIBLE_TAGS);

      let used = 0;
      let count = 0;

      for (const width of widths) {
        const next = used + (count > 0 ? TAG_GAP : 0) + width;

        if (next > available) {
          break;
        }

        used = next;
        count += 1;
      }

      while (count > 0 && count < allWidths.length) {
        if (used + TAG_GAP + toggleWidth <= available) {
          break;
        }

        used -= (widths[count - 1] ?? 0) + (count > 1 ? TAG_GAP : 0);
        count -= 1;
      }

      setVisibleCount(count);
    };

    recalculate();

    const observer = new ResizeObserver(recalculate);
    observer.observe(row);
    observer.observe(measure);

    return () => observer.disconnect();
  }, []);

  if (tags.length === 0) {
    return null;
  }

  const hiddenCount = tags.length - visibleCount;

  return (
    <div className={cn("relative", className)}>
      <div
        aria-hidden="true"
        className="pointer-events-none invisible absolute flex w-max gap-2"
        ref={measureRef}
      >
        {tags.map((tag) => (
          <span className={tagClassName} key={tag}>
            {tag}
          </span>
        ))}
        <span className={tagClassName}>+{tags.length}</span>
      </div>
      <div
        className={cn(
          "flex gap-2",
          isExpanded ? "flex-wrap" : "overflow-hidden",
        )}
        ref={rowRef}
      >
        {(isExpanded ? tags : tags.slice(0, visibleCount)).map((tag) => (
          <span className={tagClassName} key={tag}>
            {tag}
          </span>
        ))}
        {!isExpanded && hiddenCount > 0 && (
          <button
            aria-label={expandLabel}
            className={cn(
              tagClassName,
              "cursor-pointer hover:bg-background-fill-primary-hover",
            )}
            onClick={() => setIsExpanded(true)}
            type="button"
          >
            +{hiddenCount}
          </button>
        )}
      </div>
    </div>
  );
};
